import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { UsersService } from 'services/users/users';
import { useDispatch } from 'react-redux';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import moment from 'moment';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import { finalize } from 'rxjs/operators';
import { translate } from 'components/Translation';
import { AccountsService } from 'services/accounts/accounts';
import { DepartmentsService } from 'services/departments/departments';
import _ from 'lodash';
import { setToast } from 'redux/modules/toast/actions';
import Wrapper from 'components/Wrapper/Wrapper';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import { SingleDepartment } from 'utils/api/models/departments/singleDepartment';
import UserDetailsHeader from 'views/Users/Details/UserDetails/subcomponents/UserDetailsHeader';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import UserInformation from 'views/Users/Details/UserDetails/subcomponents/UserInformation';
import Dialogs from 'views/Users/Details/UserDetails/subcomponents/Dialogs';
import { SinglePermission } from 'utils/api/models/users/singlePermission';
import invalidateCache from 'utils/api/invalidateCache';

interface ILoading {
	departments: boolean;
	accounts: boolean;
}

interface IExtendedSingleDepartments extends SingleDepartment {
	permission: types.users.IExtendedSinglePermission;
}

interface IAccountsWithDepartmentsDetails extends SingleCorpAccount {
	permission: types.users.IExtendedSinglePermission;
	departments: IExtendedSingleDepartments[];
}

interface IAccountsWithDepartments {
	[accountId: string]: IAccountsWithDepartmentsDetails;
}

interface IDetailsProps {
	match: {
		params: {
			userId: string;
		};
	};
	user: types.users.IExtendedCorpUserDetails;
	setUser: (
		user:
			| types.users.IExtendedCorpUserDetails
			| ((user: types.users.IExtendedCorpUserDetails) => types.users.IExtendedCorpUserDetails)
	) => void;
}

const UserDetails = ({ user, setUser, match }: IDetailsProps) => {
	const [dialog, setDialog] = useState<types.users.editDialog>(null);
	const [accounts, setAccounts] = useState<SingleCorpAccount[]>([]);
	const [departments, setDepartments] = useState<SingleDepartment[]>([]);
	const [editData, setEditData] = useState<types.users.IEditData>({
		accountId: '',
		username: '',
		email: '',
		departmentId: '',
		contactNumber: '',
	});
	const [editPermissionAccount, setEditPermissionAccount] = useState<types.users.IEditPermissionAccount | null>(null);
	const [loading, setLoading] = useState<ILoading>({
		departments: true,
		accounts: true,
	});
	const [editTypeForm, setEditTypeForm] = useState<{ type: types.users.userType }>({ type: 'ENDUSER' });
	const { userId } = match.params;
	const canPerformAction: string = `user-details:edit-${(
		user?.userData?.userType || 'SUPERADMIN'
	).toLowerCase()}-user`;
	const dispatch = useDispatch();
	const isAdmin = user && user.userData.userType === 'ADMIN';
	const fetchingData: boolean = Object.keys(loading).some((key) => loading[key]);

	const closeDialog = (): void => {
		setDialog(null);
	};

	const resendEmailOnClick = (): void => {
		dispatch(showLoadingIndicator());
		UsersService.resendActivationEmailRestPost(userId)
			.pipe(finalize(() => dispatch(hideLoadingIndicator())))
			.subscribe(({ response }) => {
				if (response.arguments.returnValue === 'true') {
					dispatch(
						setToast({
							error: false,
							label: translate('user.details.activation.email.toast.message'),
							actionType: translate('user.details.activation.email.toast.title'),
						})
					);
					setUser({
						...user,
						userData: {
							...user.userData,
							lastSentActivationEmailDate: moment().format('LLL'),
						},
					});
				} else {
					dispatch(
						setToast({
							error: true,
							label: translate('error.invalid.request'),
							actionType: translate('error'),
						})
					);
				}
			});
	};

	const accountsWithDepartments: IAccountsWithDepartments = useMemo(() => {
		const permissions = _.get(user, 'permissions') || [];
		const accountsMap = accounts.reduce(
			(acc, account) => ({
				...acc,
				[account.accountId]: {
					...account,
					permission: permissions.find(
						({ type, accountId }) => type === 'A' && accountId === account.accountId
					),
				},
			}),
			{}
		);

		return departments.reduce((accounts, department) => {
			const account = accounts[department.accountId];

			if (!account) {
				return accounts;
			}
			return {
				...accounts,
				[account.accountId]: {
					...account,
					departments: [
						..._.get(account, 'departments', []),
						{
							...department,
							permission: permissions.find(
								({ type, departmentId }) => type === 'D' && departmentId === department.deptId
							),
						},
					],
				},
			};
		}, accountsMap);
	}, [accounts, departments, user]);

	const resetEditData = useCallback(() => {
		const data: types.users.IEditData = user
			? {
					username: user.userData.userName,
					email: user.userData.email,
					departmentId: user.userData.departmentId,
					accountId: user.userData.accountId,
					contactNumber: user.userData.contactNumber1,
			  }
			: {
					username: '',
					email: '',
					departmentId: '',
					accountId: '',
					contactNumber: '',
			  };
		setEditData(data);
	}, [user, setEditData]);

	useEffect(() => {
		resetEditData();
	}, [resetEditData]);

	const handleEditRequest = (): void => {
		dispatch(showLoadingIndicator());
		UsersService.updateCorpUserInformation({
			...editData,
			userId,
		})
			.pipe(finalize(() => dispatch(hideLoadingIndicator())))
			.subscribe(() => {
				closeDialog();
				const department =
					editData.departmentId && departments.find(({ deptId }) => deptId === editData.departmentId);

				dispatch(
					setToast({
						error: false,
						label: ([
							user.userData.email !== editData.email && 'email.address',
							user.userData.userName !== editData.username && 'username',
							editData.departmentId !== user.userData.departmentId && 'department',
							editData.contactNumber !== user.userData.contactNumber1 && 'contact.number',
						].filter(Boolean) as string[])
							.map((key) => translate(key))
							.join(','),
						actionType: translate('edited'),
					})
				);

				setUser((user) => ({
					...user,
					permissions: user.permissions || [],
					userData: {
						...user.userData,
						userName: editData.username,
						email: editData.email,
						contactNumber1: editData.contactNumber,
						accountId: editData.accountId,
						...(({ accountName }) => ({ accountName }))(accountsWithDepartments[editData.accountId]),
						...(department
							? {
									departmentId: department.deptId,
									departmentName: department.name,
									departmentUiColor: department.uiColor,
							  }
							: {
									departmentId: '',
									departmentName: '',
									departmentUiColor: '',
							  }),
					},
				}));
			});
	};

	const handleEditTypeRequest = () => {
		dispatch(showLoadingIndicator());
		UsersService.updateCorpUserType({
			userId,
			userType: editTypeForm.type,
		})
			.pipe(finalize(() => dispatch(hideLoadingIndicator())))
			.subscribe(
				() => {
					getCorpUserDetails();
					closeDialog();
					dispatch(
						setToast({
							error: false,
							actionType: translate('edited'),
							label: translate(
								'users.details.userTypeChanged',
								translate(`role.${editTypeForm.type}`).toLowerCase()
							),
						})
					);
				},
				(error) => {
					closeDialog();
					dispatch(
						setToast({
							error: true,
							actionType: translate('error'),
							label: changeUserTypeError(error?.response?.code),
						})
					);
				}
			);
	};

	const getCorpUserDetails = () => {
		invalidateCache(['getCorpUserDetails'], true);
		UsersService.getCorpUserDetails(userId).subscribe((response) => {
			setUser(() => ({
				...response,
				permissions: (response.permissions || []).map((permission: SinglePermission) => ({
					...permission,
					permissionExpiration: permission.permissionExpiration
						? moment(permission.permissionExpiration).format('YYYY-MM-DD')
						: undefined,
				})),
			}));
		});
	};

	const changeUserTypeError = (code?: string) => {
		switch (code) {
			case 'MAXIMUM_ADMINS_LIMIT_EXCEEDED':
				return translate('error.maximum.admins.limit.exceeded');
			case 'EMAIL_NOT_FOUND':
				return translate('users.list.change.user.type.email.not.set');
			case 'ENDUSER_EMAIL_EXIST_AS_OTHER_ACCOUNT':
				return translate('users.list.change.user.type.email.exist.as.other.account');
			default:
				return translate('error.invalid.request');
		}
	};

	const handleDeletePermissionsRequest = () => {
		if (editPermissionAccount) {
			dispatch(showLoadingIndicator());
			UsersService.deleteAccountPermissionsForCorpUsers({
				userIds: [userId],
				accountId: editPermissionAccount.accountId || '',
			})
				.pipe(finalize(() => dispatch(hideLoadingIndicator())))
				.subscribe(() => {
					closeDialog();

					dispatch(
						setToast({
							error: true,
							actionType: translate('deleted'),
							label: translate(
								'account.permissions',
								accountsWithDepartments[editPermissionAccount.accountId].accountName
							),
						})
					);
					setUser((user) => ({
						...user,
						permissions: user.permissions.filter((permission) => {
							return permission.accountId !== editPermissionAccount.accountId;
						}),
					}));

					//isAdmin - we have to decrease the counter
					if (isAdmin) {
						setAccounts((accounts) =>
							accounts.map((account) => {
								if (account.accountId !== editPermissionAccount.accountId) {
									return account;
								}
								return { ...account, adminsCount: account.adminsCount - 1 };
							})
						);
					}
				});
		}
	};

	const handleSaveAddData = (permissions: types.users.IExtendedSinglePermission[]) => {
		dispatch(showLoadingIndicator());
		closeDialog();
		UsersService.addCorpUserPermissions(userId, permissions)
			.pipe(finalize(() => dispatch(hideLoadingIndicator())))
			.subscribe(() => {
				const accounts = [...new Set(permissions.map(({ accountId }) => accountId))].map(
					(id) => accountsWithDepartments[id]
				);
				dispatch(
					setToast({
						error: false,
						label: translate(
							'account.permissions',
							accounts.map(({ accountName }) => accountName).join(',')
						),
						actionType: translate('added'),
					})
				);
				setUser((user) => ({
					...user,
					permissions: [...user.permissions, ...permissions],
				}));
			});
	};

	const handleSaveAddBillingAccountPermission = (permissions: types.users.IExtendedSinglePermission[]) => {
		dispatch(showLoadingIndicator());
		closeDialog();
		UsersService.addCorpUserPermissions(userId, permissions)
			.pipe(finalize(() => dispatch(hideLoadingIndicator())))
			.subscribe(() => {
				dispatch(
					setToast({
						error: false,
						label: translate(
							'users.permissions.topup.updated',
							accounts.map(({ accountName }) => accountName).join(',')
						),
						actionType: translate('updated'),
					})
				);
				setUser((user) => ({
					...user,
					permissions: [...user.permissions, ...permissions],
				}));
			});
	};

	const handleUpdateBillingAccountPermission = (permissions: types.users.IExtendedSinglePermission[]) => {
		dispatch(showLoadingIndicator());
		closeDialog();
		UsersService.updateCorpUserPermissions({ userId, override: true, permissions })
			.pipe(finalize(() => dispatch(hideLoadingIndicator())))
			.subscribe(() => {
				dispatch(
					setToast({
						error: false,
						label: translate('users.permissions.topup.updated'),
						actionType: translate('updated'),
					})
				);
				setUser((user) => ({
					...user,
					permissions: permissions,
				}));
			});
	};

	const handleSaveEditData = (): void => {
		if (user.userData.departmentId === editData.departmentId || !user.userData.departmentId) {
			handleEditRequest();
		} else {
			setDialog('editUserDepartmentConfirmation');
		}
	};

	const handleEditPermission = (accountId: string): void => {
		let accountPermission = accountsWithDepartments[accountId].permission;
		const preparePermission = (permission: types.users.IExtendedSinglePermission) => ({
			...permission,
			isUnlimited: !permission.permissionExpiration,
		});

		if (accountPermission) {
			accountPermission = preparePermission(accountPermission);
		}

		setEditPermissionAccount({
			accountId,
			account: accountPermission,
			departments: (accountsWithDepartments?.[accountId]?.departments || [])
				.filter(({ permission }) => !!permission)
				.reduce(
					(acc, department) => ({
						...acc,
						[department.deptId]: preparePermission(department.permission),
					}),
					{}
				),
		});

		setDialog('editPermission');
	};

	const handleEditPermissionRequest = () => {
		closeDialog();
		if (editPermissionAccount) {
			const newData = [editPermissionAccount.account, Object.values(editPermissionAccount.departments)]
				.filter(Boolean)
				.flat()
				.map((permission) => {
					if (permission.isUnlimited) {
						return _.omit(permission, ['permissionExpiration']);
					}
					return permission;
				});

			const prepareData = {
				D: ['departmentId'],
				A: ['accountId'],
			};
			dispatch(showLoadingIndicator());
			const persistedBillingAccountPermissions = user.permissions.filter((permission) => permission.type === 'B');
			const persistedOtherPermissions = user.permissions.filter(
				(permission) => permission.type !== 'B' && permission.accountId !== editPermissionAccount.accountId
			);
			const pickedPersistedOtherPermission = persistedOtherPermissions.map((permission) => {
				return {
					accountId: permission.accountId,
					departmentId: permission.departmentId,
					type: permission.type,
				} as types.users.IExtendedSinglePermission;
			});
			UsersService.updateCorpUserPermissions({
				userId,
				override: true,
				permissions: newData
					.map((permission) => {
						return {
							..._.pick(permission, prepareData[permission.type]),
							type: permission.type,
							accountId: editPermissionAccount.accountId,
							permissionExpiration: permission.permissionExpiration,
						} as types.users.IExtendedSinglePermission;
					})
					.concat(persistedBillingAccountPermissions)
					.concat(pickedPersistedOtherPermission),
			})
				.pipe(finalize(() => dispatch(hideLoadingIndicator())))
				.subscribe(() => {
					setUser((user) => ({
						...user,
						permissions: newData
							.concat(persistedBillingAccountPermissions)
							.concat(persistedOtherPermissions),
					}));
				});
		}
	};

	const resetEditType = useCallback(
		() => setEditTypeForm(user ? { type: user.userData.userType } : { type: 'ENDUSER' }),
		[user]
	);

	const handleSaveEditUserAccount = () => {
		const { accountId, departmentId } = user.userData;
		if (accountId !== editData.accountId || departmentId !== editData.departmentId) {
			if (!departmentId) {
				setDialog('setUserDepartmentConfirmation');
			} else if (accountId !== editData.accountId) {
				setDialog('editUserAccountAccountsConfirmation');
			} else {
				setDialog('editUserDepartmentConfirmation');
			}
		} else {
			return null;
		}
	};

	useEffect(() => {
		user && resetEditData();
		user && resetEditType();
	}, [user, resetEditData, resetEditType]);

	useEffect(() => {
		dispatch(showLoadingIndicator());
		setLoading({
			departments: true,
			accounts: true,
		});

		AccountsService.getCorpAccountsWithAdminsForCurrentUser()
			.pipe(finalize(() => setLoading((loading) => ({ ...loading, accounts: false }))))
			.subscribe(({ accounts }) => setAccounts(accounts));

		DepartmentsService.getDepartmentsForCurrentUser()
			.pipe(finalize(() => setLoading((loading) => ({ ...loading, departments: false }))))
			.subscribe(({ departments }) => setDepartments(departments.filter((department) => department.deptId)));

		return () => {
			dispatch(hideLoadingIndicator());
		};
	}, [userId, dispatch]);

	useEffect(() => {
		if (Object.keys(loading).every((key) => !loading[key])) {
			dispatch(hideLoadingIndicator());
		}
	}, [loading, dispatch]);

	const accountsWithoutPermissions: SingleCorpAccount[] = useMemo(() => {
		if (!user) {
			return [];
		}
		const uniqueIds = [
			...new Set(
				user.permissions.filter((permission) => permission.type === 'D').map(({ accountId }) => accountId)
			),
		];
		return accounts.filter(({ accountId }) => !uniqueIds.includes(accountId));
	}, [accounts, user]);

	const handleAddEmail = (newEmail: string) => {
		UsersService.updateCorpUserInformation({
			userId: user.userData.userId,
			username: user.userData.userName,
			email: newEmail,
			accountId: user.userData.accountId,
		}).subscribe(
			({ response }) => {
				if (response.updateCorpUserInformationResponse.result) {
					setDialog('editUserTypeConfirmation');
				}
			},
			(error) => {
				changeUserTypeError('DID_NOT_ADD_EMAIL');
			}
		);
	};

	const handleDeletePermission = (accountId: string, userId: string) => {
		dispatch(showLoadingIndicator());
		const data = {
			userId: userId,
			permissions: {
				accountId: accountId,
				type: 'A',
			},
		};
		UsersService.deleteCorpUserPermissions(data)
			.pipe(
				finalize(() => {
					dispatch(hideLoadingIndicator());
					setLoading((loading) => ({ ...loading, accounts: false }));
				})
			)
			.subscribe(({ response }) => {
				const newPermissions = user.permissions.filter((p) => p.accountId !== accountId);
				setUser({ ...user, permissions: newPermissions });
			});
	};

	return (
		<ConditionalRender
			show={!fetchingData}
			onTrue={() => (
				<ConditionalRender
					show={!!user}
					onTrue={() => (
						<>
							<BreadcrumbsItem to={`/dashboard/users/${userId}`}>
								{user.userData.userName} - {translate('path.user.details')}
							</BreadcrumbsItem>
							<Dialogs
								dialog={dialog}
								setDialog={setDialog}
								editPermissionAccount={editPermissionAccount}
								setEditPermissionAccount={setEditPermissionAccount}
								closeDialog={closeDialog}
								departments={departments}
								accountsWithoutPermissions={accountsWithoutPermissions}
								resetEditData={resetEditData}
								handleDeletePermissionsRequest={handleDeletePermissionsRequest}
								accounts={accounts}
								resetEditType={resetEditType}
								user={user}
								handleEditRequest={handleEditRequest}
								editData={editData}
								setEditData={setEditData}
								editTypeForm={editTypeForm}
								setEditTypeForm={setEditTypeForm}
								handleSaveAddData={handleSaveAddData}
								handleSaveAddBillingAccountPermission={handleSaveAddBillingAccountPermission}
								handleUpdateBillingAccountPermission={handleUpdateBillingAccountPermission}
								handleEditPermissionRequest={handleEditPermissionRequest}
								handleEditTypeRequest={handleEditTypeRequest}
								handleSaveEditUserAccount={handleSaveEditUserAccount}
								handleSaveEditData={handleSaveEditData}
								accountsWithDepartments={accountsWithDepartments}
								handleAddEmail={(newEmail) => handleAddEmail(newEmail)}
							/>
							<UserDetailsHeader
								user={user}
								canPerformAction={canPerformAction}
								openEditUserDialog={() => setDialog('editUser')}
							/>
							<UserInformation
								user={user}
								accounts={accounts}
								departments={departments}
								canPerformAction={canPerformAction}
								setDialog={setDialog}
								resendEmailOnClick={resendEmailOnClick}
								handleEditPermission={handleEditPermission}
								accountsWithoutPermissions={accountsWithoutPermissions}
								handleDeletePermission={handleDeletePermission}
							/>
						</>
					)}
				/>
			)}
		/>
	);
};

export default Wrapper(UserDetails, true);
